import { useMemo } from 'react';

import { type Props as PermissionsProps, usePermissions } from './use-permissions.hook';

export const createUserPermissions = (permissions: ReturnType<typeof usePermissions>) => {
	const { hasAnyPermission, usePdf, useExport, useImport, useBlockContacts, useCustomRoles } = permissions;

	const canAdmin = () => hasAnyPermission('CUSTOMERS_ADMIN');

	// PDFs of any contact, or of the signed-in user's own contact only.
	const canGenerateDocumentAny = () => !!usePdf && hasAnyPermission('CUSTOMERS_DOCUMENT_GENERATOR');
	const canGenerateDocumentOwn = () =>
		!!usePdf && hasAnyPermission('CUSTOMERS_DOCUMENT_GENERATOR', 'CUSTOMERS_OWN_DOCUMENT_GENERATOR');

	// Login and seat administration (invite, sync, operator seat) is a users right, not a contacts right.
	const canAdminUsers = () => hasAnyPermission('USERS_ADMIN');

	const canBlock = () => !!useBlockContacts && hasAnyPermission('CUSTOMERS_BLOCK_ADMIN');

	// Reset password sends `users/:id/call/inviteUser`, which python-api gates on USERS_ADMIN only
	// (as activate / deactivate / archive); the profile admin rights never count.
	const canResetPassword = () => hasAnyPermission('USERS_ADMIN');
	// python-api `updateProfile`: USERS_PROFILE_ADMIN for anybody, USERS_OWN_PROFILE_ADMIN for yourself.
	const canUpdateProfile = (ownUser?: boolean) =>
		hasAnyPermission('USERS_PROFILE_ADMIN') || (!!ownUser && hasAnyPermission('USERS_OWN_PROFILE_ADMIN'));

	const userPermissions = {
		canRead: () => hasAnyPermission('CUSTOMERS_READER'),
		canCreate: canAdmin,
		/** `ownContact`: the signed-in user's own contact, which CUSTOMERS_OWN_ADMIN may edit (python-api `update`). */
		canUpdate: (ownContact?: boolean) => !!canAdmin() || (!!ownContact && hasAnyPermission('CUSTOMERS_OWN_ADMIN')),
		canDelete: canAdmin,
		canArchive: canAdmin,
		// python-api `attach`: the any-contact writer, or the own writer on the own contact.
		canAddAttachment: (ownUser?: boolean) =>
			hasAnyPermission('CUSTOMERS_ATTACHMENTS_OWN_WRITER') ||
			(!!ownUser && hasAnyPermission('CUSTOMERS_OWN_ATTACHMENTS_OWN_WRITER')),
		// python-api `detach` (CHQ-8351): the deleter removes any attachment; the own deleter your own
		// attachment on any contact; the own-own deleter your own attachment on your own contact.
		canRemoveAttachment: (ownAttachment?: boolean, ownUser?: boolean) =>
			hasAnyPermission('CUSTOMERS_ATTACHMENTS_DELETER') ||
			(!!ownAttachment && hasAnyPermission('CUSTOMERS_ATTACHMENTS_OWN_DELETER')) ||
			(!!ownAttachment && !!ownUser && hasAnyPermission('CUSTOMERS_OWN_ATTACHMENTS_OWN_DELETER')),
		canAddComment: (ownUser?: boolean) =>
			hasAnyPermission('CUSTOMERS_COMMENTS_OWN_WRITER') ||
			(!!ownUser && hasAnyPermission('CUSTOMERS_OWN_COMMENTS_OWN_WRITER')),
		canEditComment: (ownComment?: boolean, ownUser?: boolean) =>
			(ownComment && hasAnyPermission('CUSTOMERS_COMMENTS_OWN_WRITER')) ||
			(ownComment && ownUser && hasAnyPermission('CUSTOMERS_OWN_COMMENTS_OWN_WRITER')),
		canRemoveComment: (ownComment?: boolean, ownUser?: boolean) =>
			hasAnyPermission('CUSTOMERS_COMMENTS_DELETER') ||
			(ownComment && hasAnyPermission('CUSTOMERS_COMMENTS_OWN_DELETER')) ||
			(ownComment && ownUser && hasAnyPermission('CUSTOMERS_OWN_COMMENTS_OWN_DELETER')),
		canSeeUsers: () => userPermissions.canRead(),
		canEditUsers: canAdmin,
		canInviteUsers: canAdminUsers,
		canChangeRole: () => !!useCustomRoles && canAdminUsers(),
		canEditUserGroups: () => hasAnyPermission('ACCOUNT_SETTINGS_ADMIN'),
		canArchiveUsers: canAdmin,
		canUndoArchiveUsers: canAdmin,
		canDeleteUsers: canAdmin,
		canBlockUsers: canBlock,
		canResetPassword,
		canUpdateProfile,
		canUnblockUsers: canBlock,
		canGenerateDocumentAny,
		canGenerateDocumentOwn,
		/** `own`: the document is for the signed-in user's own contact. */
		canGenerateDocument: (own?: boolean) => canGenerateDocumentAny() || (!!own && canGenerateDocumentOwn()),
		// python-api `changeKind`: CUSTOMERS_ADMIN; the maintenance kind also needs CUSTOMERS_MAINTENANCE_ADMIN.
		canChangeKind: () => hasAnyPermission('CUSTOMERS_ADMIN'),
		canChangeKindMaintenance: () =>
			hasAnyPermission('CUSTOMERS_ADMIN') && hasAnyPermission('CUSTOMERS_MAINTENANCE_ADMIN'),
		canClearSync: canAdminUsers,
		canChangeOperator: canAdminUsers,
		canExportUsers: () => !!useExport && hasAnyPermission('CUSTOMERS_EXPORTER'),
		canImportUsers: () => !!useImport && hasAnyPermission('CUSTOMERS_IMPORTER'),
		canCreateUsers: canAdmin,
		canChangeCover: canAdmin,
	};

	return userPermissions;
};

export const useUserPermissions = (props: PermissionsProps) => {
	const permissions = usePermissions(props);

	return useMemo(() => createUserPermissions(permissions), [permissions]);
};
